import React from 'react'
import './style/accounts.scss'
import { Link } from 'react-router-dom'
import { Button, PageHeader } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { observer } from 'mobx-react'
import { Loading3QuartersOutlined, WarningOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import '@ant-design/compatible/assets/index.css'
import { Table, Input, Tag, Modal, Menu, Dropdown, Form, FormInstance, Tooltip, Select } from 'antd'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { ColumnProps, TablePaginationConfig } from 'antd/lib/table'
import * as moment from 'moment-timezone'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { simpleNotification } from '@halo/_utils/notifications'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { RuleObject } from 'antd/lib/form'
import { CurrentUserCan } from '@halo/stores/ability.state'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { AppState } from '@halo/stores/app.state'
import DataTableView from '@halo/components/data-table-view/data-table-view'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'
import { Select as AqeSelect } from '@halo/components/select/select'
import { StatusType } from '@pushly/aqe/lib/enums'
import deepEqual from 'react-fast-compare'
import { fetchWhiteLabels } from '@halo/api/white-labels.api'
import WhiteLabelModel from '@halo/models/white-label.model'

export const ORGANIZATIONS_ROUTE = '/organizations'

class AccountTableDataDto extends AccountDto {
    public static fromAccountDto(fields: Partial<AccountTableDataDto>): AccountTableDataDto {
        const dto = new AccountTableDataDto()
        Object.assign(dto, fields, {
            key: fields.id,
        })
        return dto
    }

    public readonly key: number
}

interface IColumnProps extends ColumnProps<AccountTableDataDto> {
    dataKey?: string
}

interface IAccounts {}

interface IAccountsState {
    loading: boolean
    accountsLoaded: boolean
    allAccounts: any[]
    visibleAccounts: any[]
    accountsMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
    showNewAccountPrompt: boolean
    statusFilter: string[]
    whiteLabels?: WhiteLabelModel[]
}

@observer
export class Accounts extends React.Component<IAccounts, IAccountsState> {
    protected appState: AppState
    protected appService: AppService
    protected orgService: AccountService

    protected newAccountFormRef = React.createRef<FormInstance>()

    public constructor(props: IAccounts) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.orgService = Container.get(AccountService)

        this.state = {
            page: 1,
            pageSize: 15,
            loading: true,
            accountsLoaded: false,
            allAccounts: [],
            visibleAccounts: [],
            accountsMeta: {},
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },
            showNewAccountPrompt: false,
            statusFilter: [StatusType[StatusType.ACTIVE]],
        }
    }

    public componentDidMount(): void {
        this.ensureAccountsForList()
        this.fetchWhitelabels()
    }

    public async ensureAccountsForList() {
        await this.fetchAccounts()
    }

    public render() {
        const pagination: any = {
            pageSize: this.state.pageSize,
            total: this.state.accountsMeta?.total,
            current: this.state.page,
            showSizeChanger: false,
            size: 'small',
            simple: true,
            showTotal: (total: number) => (
                <span>
                    Total ${total} {total === 1 ? 'item' : 'items'}
                </span>
            ),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }

                return originalElement
            },
            onChange: async (page: number, pageSize: number) => {
                await this.setState({ page })
                await this.fetchAccounts()
            },
        }

        return (
            <div className={classnames('page', 'accounts')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader title="Organizations" onTitleSet={noop} />

                    <DataTableView
                        searchPlaceholder="Search ..."
                        onSearchChange={this.searchAccounts}
                        actions={
                            <CurrentUserCan do={AbilityAction.CREATE} on={SubjectEntity.ORG}>
                                <Button type="primary" size="small" onClick={this.showNewOrgPrompt}>
                                    New Org
                                </Button>
                            </CurrentUserCan>
                        }
                        filtersOverride={
                            <React.Fragment>
                                <div className="data-table-filter">
                                    <AqeSelect
                                        className="status-filter data-table-filter-select"
                                        dropdownClassName="data-table-filter-select-overlay"
                                        mode="multiple"
                                        size="small"
                                        prefix="Status"
                                        disableSearch={true}
                                        disableSelectAll={true}
                                        defaultValue={this.state.statusFilter}
                                        options={[
                                            { value: StatusType[StatusType.ACTIVE], label: 'Active' },
                                            { value: StatusType[StatusType.DISABLED], label: 'Disabled' },
                                        ]}
                                        onClose={this.filterAccountStatuses}
                                        maxDisplayCount={2}
                                        maxDisplayFormatter={(options: any[]) => {
                                            return (
                                                <Tooltip title={options.map((o) => o.label).join(', ')}>
                                                    {options.length} Selected
                                                </Tooltip>
                                            )
                                        }}
                                    />
                                </div>
                            </React.Fragment>
                        }
                    >
                        <Table<AccountTableDataDto>
                            size="small"
                            bordered={false}
                            dataSource={this.state.visibleAccounts}
                            rowKey={(row) => row.id}
                            onChange={this.handleChange}
                            pagination={false}
                            footer={(currentTableData) => (
                                <SwSimpleLinkPagination
                                    currentTableData={currentTableData}
                                    links={this.state.paginationLinks}
                                    onPrev={(_ev, _link, config) => {
                                        this.setState({ page: config.current! }, () => this.fetchAccounts())
                                    }}
                                    onNext={(_ev, _link, config) => {
                                        this.setState({ page: config.current! }, () => this.fetchAccounts())
                                    }}
                                />
                            )}
                            loading={
                                this.isLoading && {
                                    indicator: <Loading3QuartersOutlined spin={true} />,
                                }
                            }
                        >
                            <Table.Column className="id" title="ID" dataIndex="id" />
                            <Table.Column
                                className="account"
                                title="Organization"
                                dataIndex="display_name"
                                render={this.renderDisplay}
                            />
                            <Table.Column
                                className="created_at"
                                title="Created"
                                dataIndex="created_at"
                                render={this.renderCreatedAt}
                            />
                            <Table.Column
                                className="actions"
                                title="Actions"
                                align="center"
                                render={(_, notification: AccountTableDataDto) => this.renderRowActions(notification)}
                            />
                        </Table>
                    </DataTableView>

                    <Modal
                        title="New Organization"
                        visible={this.state.showNewAccountPrompt}
                        onOk={this.handleCreateNewAccount}
                        onCancel={this.hideNewOrgPrompt}
                    >
                        <Form ref={this.newAccountFormRef}>
                            <Form.Item
                                name="name"
                                label="Name"
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_: RuleObject, value) => {
                                            if (!value) {
                                                return Promise.reject(new Error('Organization name is required.'))
                                            } else {
                                                this.newAccountFormRef.current.setFieldsValue({ name: value.trim() })
                                                return Promise.resolve()
                                            }
                                        },
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="platformWhitelabelId"
                                label="Platform Whitelabel"
                                initialValue={this.state.whiteLabels
                                    ?.find((val) => val.getIsDefault() === true)
                                    ?.getId()}
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_: RuleObject, value) => {
                                            if (!value) {
                                                return Promise.reject(
                                                    new Error('Organization platform whitelabel is required.'),
                                                )
                                            } else {
                                                this.newAccountFormRef.current.setFieldsValue({
                                                    platformWhitelabel: value,
                                                })
                                                return Promise.resolve()
                                            }
                                        },
                                    },
                                ]}
                            >
                                <Select>
                                    {this.state.whiteLabels?.map((whiteLabel: WhiteLabelModel) => {
                                        return (
                                            <Select.Option key={whiteLabel.getId()} value={whiteLabel.getId()}>
                                                {whiteLabel.getDisplayName()}
                                            </Select.Option>
                                        )
                                    })}
                                </Select>
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            </div>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loading && !this.state.accountsLoaded
    }

    protected handleDateSort = (a: AccountTableDataDto, b: AccountTableDataDto): number => {
        const aCreated = moment(a.created_at)
        const bCreated = moment(b.created_at)

        return aCreated > bCreated ? 1 : aCreated < bCreated ? -1 : 0
    }

    protected renderDisplay = (text: string, account: AccountTableDataDto): React.ReactNode => {
        const name = account.name

        return (
            <TableRowEntityDisplay
                title={<Link to={this.buildOrgUrl(account)}>{name}</Link>}
                status={<StatusBadge type="account" status={account.status} expanded={true} />}
            />
        )
    }

    protected renderStatus = (text: string, account: AccountTableDataDto): React.ReactNode => {
        let value: any = ''

        if (account.status === 'ACTIVE') {
            value = <Tag color="green">Active</Tag>
        } else {
            value = <Tag color="red">Disabled</Tag>
        }

        return value
    }

    protected renderCreatedAt = (text: string, account: AccountTableDataDto): React.ReactNode => {
        return moment(text).format('YYYY/MM/DD')
    }

    protected renderRowActions(account: AccountTableDataDto): React.ReactNode {
        const actions: any[] = []

        if (account.status === 'ACTIVE') {
            actions.push({
                key: 'disable',
                text: 'Disable',
                icon: <EyeOutlined />,
            })
        }

        if (account.status === 'DISABLED') {
            actions.push({
                key: 'enable',
                text: 'Enable',
                icon: <EyeOutlined />,
            })
        }

        return (
            <Dropdown.Button
                onClick={(ev) => this.handlePrimaryActionClick(account, ev)}
                size="small"
                className="list-link-button"
                href={this.buildOrgUrl(account)}
                overlay={
                    actions.length === 0 ? undefined : (
                        <Menu onClick={({ key, domEvent }) => this.handleActionClick(key, account, domEvent)}>
                            {actions.map((item) => (
                                <Menu.Item key={item.key}>
                                    {item.icon}
                                    <span>{item.text}</span>
                                </Menu.Item>
                            ))}
                        </Menu>
                    )
                }
            >
                View
            </Dropdown.Button>
        )
    }

    protected buildOrgUrl(org: AccountDto): string {
        return `/organizations/${org.id}`
    }

    protected jumpToOrg(org: AccountDto): void {
        this.appService.route(this.buildOrgUrl(org))
    }

    protected showNewOrgPrompt = () => {
        this.setState({ showNewAccountPrompt: true })
    }

    protected hideNewOrgPrompt = () => {
        this.newAccountFormRef.current?.setFieldsValue?.({ name: undefined })
        this.setState({ showNewAccountPrompt: false })
    }

    protected handleCreateNewAccount = async (): Promise<void> => {
        const showError = () =>
            simpleNotification('error', 'Cannot process new organization at this time. Please try again later.')

        if (this.newAccountFormRef.current) {
            try {
                const values = await this.newAccountFormRef.current.validateFields()

                const { ok, data } = await this.orgService.create({
                    name: values.name,
                    platformWhitelabelId: values.platformWhitelabelId,
                })

                if (ok) {
                    this.hideNewOrgPrompt()
                    this.jumpToOrg(data)
                }
            } catch (_) {}
        } else {
            showError()
        }
    }

    protected handleChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<AccountTableDataDto>,
        _extra: TableCurrentDataSource<AccountTableDataDto>,
    ) => {
        this.setState({ sortedInfo: sorter })
        this.fetchAccounts()
    }

    protected handlePrimaryActionClick(account: AccountTableDataDto, event: React.MouseEvent<HTMLButtonElement>): void {
        if (event.metaKey) {
            window.open(this.buildOrgUrl(account))
        } else {
            event.preventDefault()
            this.appService.route(this.buildOrgUrl(account), { account })
        }
    }

    protected handleActionClick = (
        key: string | number,
        account: AccountTableDataDto,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'disable') {
            const formRef = React.createRef<FormInstance>()

            Modal.confirm({
                title: 'Disable Org',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current

                    if (!!form) {
                        await form.validateFields()
                        return this.handleDisable(account)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>
                            Are you sure you want to disable this organization? All associated domains will be disabled
                            as well.
                        </p>

                        <p>
                            Please type the Organization name, <b>{account.name}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback('Account name is required.')
                                        } else if (value !== account.name) {
                                            callback('Account name does not match.')
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'enable') {
            Modal.confirm({
                title: 'Enable Org',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => this.handleEnable(account),
                content: (
                    <p>
                        Are you sure you want to enable this organization? All associated domains will be enabled as
                        well. Individual domains can be disabled through the domains listing.
                    </p>
                ),
            } as any)
        }
    }

    protected handleDisable = async (account: AccountTableDataDto) => {
        await this.orgService.patch(account.id, {
            status: 'DISABLED',
        })

        return this.fetchAccounts()
    }

    protected handleEnable = async (account: AccountTableDataDto) => {
        await this.orgService.patch(account.id, {
            status: 'ACTIVE',
        })

        return this.fetchAccounts()
    }

    protected filterAccountStatuses = async (statusFilter: string[]) => {
        if (!deepEqual(statusFilter, this.state.statusFilter)) {
            await this.setState({ statusFilter })

            return this.fetchAccounts()
        }
    }

    protected searchAccounts = async (data: string | any) => {
        const value = typeof data !== 'string' ? data.target.value : data

        if (!value || value.length === 0 || value.length >= 2) {
            await this.setState({ page: 1, query: value })
            await this.fetchAccounts(value)
        }
    }

    protected async fetchAccounts(search?: any) {
        this.setState({ loading: true, accountsLoaded: false })
        let update: Partial<IAccountsState>

        try {
            const { data, meta } =
                (await this.orgService.fetchAll({
                    query: {
                        limit: this.state.pageSize,
                        page: this.state.page,
                        search: search ? search : this.state.query,
                        pagination: true,
                        status: this.state.statusFilter.join(','),
                    },
                    showLoadingScreen: false,
                })) || {}

            const accountDtos: AccountDto[] = data.map((account: AccountDto) => {
                return AccountTableDataDto.fromAccountDto(account)
            })

            update = {
                accountsLoaded: true,
                allAccounts: accountDtos,
                visibleAccounts: accountDtos,
                accountsMeta: meta,
                paginationLinks: meta.links ?? this.state.paginationLinks,
            }
        } catch (error) {
            console.log(error)
        }

        this.setState({
            loading: false,
            ...(update as any),
        })
    }

    protected async fetchWhitelabels() {
        let whiteLabels
        const res = await fetchWhiteLabels({
            showLoadingScreen: false,
        })

        if (res?.ok) {
            whiteLabels = res.data?.map(WhiteLabelModel.build) ?? []
        }

        this.setState({ whiteLabels })
    }
}
